import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { X, AlertTriangle } from 'lucide-react';
import {
  getAllCommunityPosts,
  createPost,
  togglePostLike,
  deletePost,
  getActiveClients,
  subscribeToPosts,
  subscribeToMembers,
} from '../services/firebase/community';
import { getAuth } from 'firebase/auth';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase';
import PortalBackground from '../components/PortalBackground';
import CommunityPageHeader from '../components/community/CommunityPageHeader';
import CommunitySearchFilterBar from '../components/community/CommunitySearchFilterBar';
import CommunityPostFeed from '../components/community/CommunityPostFeed';
import CommunityMembersFab from '../components/community/CommunityMembersFab';
import CommunityMembersModal from '../components/community/CommunityMembersModal';
import CommunityNewPostModal from '../components/community/CommunityNewPostModal';
import { communityFileIsRasterImagePreview } from '../components/community/CommunityAttachmentThumb';
import { COMMUNITY_POSTS_PAGE_SIZE } from '../utils/communityConstants';
import { filterCommunityPosts, filterCommunityMembers } from '../utils/communityFilters';

const CommunityPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const initialPillar = new URLSearchParams(location.search).get('pillar') || 'all';

  const [activeType, setActiveType] = useState('all');
  const [activePillar, setActivePillar] = useState(initialPillar);
  const [showNewPostModal, setShowNewPostModal] = useState(false);
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [memberSearchQuery, setMemberSearchQuery] = useState('');
  const [newPost, setNewPost] = useState({
    title: '',
    description: '',
    files: [],
    type: 'general',
    category: 'sales',
  });
  const [posts, setPosts] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSubmittingPost, setIsSubmittingPost] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [postToDelete, setPostToDelete] = useState(null);
  const [postSearchQuery, setPostSearchQuery] = useState('');
  const [visibleCount, setVisibleCount] = useState(COMMUNITY_POSTS_PAGE_SIZE);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [attachmentLightboxUrl, setAttachmentLightboxUrl] = useState(null);

  const auth = getAuth();

  const filteredPosts = filterCommunityPosts(posts, {
    search: postSearchQuery,
    type: activeType,
    pillar: activePillar,
  });

  const filteredMembers = filterCommunityMembers(members, memberSearchQuery);

  useEffect(() => {
    if (!attachmentLightboxUrl) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setAttachmentLightboxUrl(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [attachmentLightboxUrl]);

  useEffect(() => {
    setVisibleCount(COMMUNITY_POSTS_PAGE_SIZE);
  }, [activeType, activePillar, postSearchQuery]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        const [postsData, membersData] = await Promise.all([getAllCommunityPosts(), getActiveClients()]);
        setPosts(postsData);
        setMembers(membersData);
      } catch (err) {
        console.error('Error loading community data:', err);
        setError('Failed to load community data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  useEffect(() => {
    const unsubscribe = subscribeToPosts((updatedPosts) => {
      setPosts(updatedPosts);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const unsubscribe = subscribeToMembers((updatedMembers) => {
      setMembers(updatedMembers);
    });
    return () => unsubscribe();
  }, []);

  const openCommunityAttachment = (file, e) => {
    if (e) e.stopPropagation();
    if (!file?.url) return;
    if (communityFileIsRasterImagePreview(file)) {
      setAttachmentLightboxUrl(file.url);
    } else {
      window.open(file.url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleNewPost = () => setShowNewPostModal(true);

  const closeModal = () => {
    setShowNewPostModal(false);
    setNewPost({ title: '', description: '', files: [], type: 'general', category: 'sales' });
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = [
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/webp',
      'application/pdf',
      'text/plain',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ];
    if (!allowedTypes.includes(file.type)) {
      alert('Please select a valid file type (images, PDF, or text files).');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB.');
      return;
    }

    try {
      setUploadingFiles(true);
      const timestamp = Date.now();
      const fileName = `community-post-${timestamp}-${file.name}`;
      const storageRef = ref(storage, `community-attachments/${fileName}`);
      const uploadResult = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(uploadResult.ref);

      const fileInfo = {
        name: file.name,
        size: file.size,
        type: file.type,
        url: downloadURL,
      };

      setNewPost((prev) => ({ ...prev, files: [...prev.files, fileInfo] }));
    } catch (uploadError) {
      console.error('Error uploading file:', uploadError);
      alert('Failed to upload file. Please try again.');
    } finally {
      setUploadingFiles(false);
    }
  };

  const removeFile = (fileName) => {
    setNewPost((prev) => ({ ...prev, files: prev.files.filter((file) => file.name !== fileName) }));
  };

  const submitPost = async () => {
    if (isSubmittingPost) return;

    try {
      if (!newPost.title.trim() || !newPost.description.trim()) {
        alert('Please fill in both title and description');
        return;
      }

      if (uploadingFiles) {
        alert('Please wait for files to finish uploading');
        return;
      }

      setIsSubmittingPost(true);
      await createPost(newPost);
      closeModal();
    } catch (submitError) {
      console.error('Error creating post:', submitError);
      alert('Failed to create post. Please try again.');
    } finally {
      setIsSubmittingPost(false);
    }
  };

  const handleLike = async (postId) => {
    try {
      await togglePostLike(postId);
    } catch (likeError) {
      console.error('Error toggling like:', likeError);
      alert('Failed to update like. Please try again.');
    }
  };

  const handleDeletePost = (postId) => {
    setPostToDelete(postId);
    setShowDeleteConfirmModal(true);
  };

  const confirmDeletePost = async () => {
    if (!postToDelete) return;

    try {
      await deletePost(postToDelete);
      setShowDeleteConfirmModal(false);
      setPostToDelete(null);
    } catch (deletePostError) {
      console.error('Error deleting post:', deletePostError);
      alert('Failed to delete post. Please try again.');
    }
  };

  const cancelDeletePost = () => {
    setShowDeleteConfirmModal(false);
    setPostToDelete(null);
  };

  const handleSendMessage = (member) => {
    if (!auth.currentUser) {
      alert('Please log in to send messages');
      return;
    }

    const memberInfo = {
      id: member.id,
      name:
        member.firstName && member.lastName
          ? `${member.firstName} ${member.lastName}`.trim()
          : member.displayName || member.email?.split('@')[0] || 'Member',
      company: member.company || 'No company',
      photoURL: member.photoURL,
    };

    localStorage.setItem('selectedMemberForMessage', JSON.stringify(memberInfo));
    navigate('/portal/messages');
  };

  return (
    <div className="min-h-screen p-4 md:p-6 relative text-gray-900 dark:text-white">
      {/* Same ambient BBA background as the Leads, Clients and Tasks pages */}
      <PortalBackground />
      <div className="relative max-w-7xl mx-auto">
        <CommunityPageHeader onNewPost={handleNewPost} />

        <CommunitySearchFilterBar
          searchQuery={postSearchQuery}
          onSearchChange={setPostSearchQuery}
          activeType={activeType}
          onTypeChange={setActiveType}
          activePillar={activePillar}
          onPillarChange={setActivePillar}
          onNewPost={handleNewPost}
        />

        <CommunityPostFeed
          posts={filteredPosts}
          loading={loading}
          error={error}
          visibleCount={visibleCount}
          totalCount={filteredPosts.length}
          onLoadMore={() => setVisibleCount((prev) => prev + COMMUNITY_POSTS_PAGE_SIZE)}
          onLike={handleLike}
          onDelete={handleDeletePost}
          onOpenAttachment={openCommunityAttachment}
        />
      </div>

      <CommunityMembersFab
        memberCount={members.length}
        onClick={() => setShowMembersModal(true)}
      />

      <CommunityMembersModal
        isOpen={showMembersModal}
        onClose={() => setShowMembersModal(false)}
        members={filteredMembers}
        searchQuery={memberSearchQuery}
        onSearchChange={setMemberSearchQuery}
        onSendMessage={handleSendMessage}
      />

      <CommunityNewPostModal
        isOpen={showNewPostModal}
        onClose={closeModal}
        newPost={newPost}
        setNewPost={setNewPost}
        onSubmit={submitPost}
        onFileChange={handleFileChange}
        onRemoveFile={removeFile}
        uploadingFiles={uploadingFiles}
        isSubmittingPost={isSubmittingPost}
      />

      {attachmentLightboxUrl && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Image preview"
          onClick={() => setAttachmentLightboxUrl(null)}
        >
          <button
            type="button"
            className="absolute top-4 right-4 rounded-full p-2 text-white/90 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white"
            onClick={() => setAttachmentLightboxUrl(null)}
            aria-label="Close preview"
          >
            <X className="w-6 h-6" />
          </button>
          <img
            src={attachmentLightboxUrl}
            alt=""
            className="max-h-[min(90vh,100%)] max-w-full object-contain shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {showDeleteConfirmModal && (
        <div className="fixed inset-0 z-50 overflow-auto bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-xl max-w-md w-full mx-auto overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800">
              <h3 className="text-lg font-semibold text-center text-gray-900 dark:text-white">
                Delete Post
              </h3>
            </div>

            <div className="px-6 py-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center bg-red-100 dark:bg-red-900/40">
                  <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    Are you sure you want to delete this post? This action cannot be undone.
                  </p>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/60 flex justify-end gap-3">
              <button
                type="button"
                onClick={cancelDeletePost}
                className="px-4 py-2 text-sm font-medium rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 shadow-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDeletePost}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-xl shadow-sm hover:bg-red-700 transition-colors"
              >
                Delete Post
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommunityPage;
